import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useUserFavoritesContext } from './contexts/UserFavoritesContext';
import { useChatSessions } from './contexts/ChatContext';
import { useViewportHeight } from './hooks/useViewportHeight';
import { MessageSquare, FileText, Heart, AlertTriangle, Menu, BarChart3, PenSquare, HeartPulse, Star, Phone, HelpCircle } from 'lucide-react';
import { hasMinimumAdminAccess } from './types/roles';
import { ConnectionStatus } from './components/ui/ConnectionStatus';
import { ContactHumanModal } from './components/ui/ContactHumanModal';
import { MoodSetupModal } from './components/user/MoodSetupModal';
import { ProfileDropdown } from './components/ui/ProfileDropdown';
import { ProfileModal } from './components/ui/ProfileModal';
import { AppFeedbackModal } from './components/ui/AppFeedbackModal';
import { Drawer } from './components/ui/Drawer';
import { MobileMenu } from './components/ui/MobileMenu';
import { SessionFeedbackModal } from './components/chat/SessionFeedbackModal';
import { SessionsDrawer } from './components/ui/SessionsDrawer';
import { HelpModal } from './components/ui/HelpModal';
import { userService } from './services/userService';

const Layout: React.FC = () => {
  // Keep viewport height in sync with visible area (handles mobile keyboard)
  useViewportHeight();

  const location = useLocation();
  const navigate = useNavigate();
  const { user, isDemoMode, signOut, backendError } = useAuth();
  const { favorites } = useUserFavoritesContext();
  const { currentSessionId, startNewSession } = useChatSessions();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isWellbeingModalOpen, setIsWellbeingModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isSessionFeedbackModalOpen, setIsSessionFeedbackModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSessionsDrawerOpen, setIsSessionsDrawerOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isMoodStale, setIsMoodStale] = useState(false);

  // Check if mood is stale on mount and when modal closes
  // Skip if there's a backend error (backend unavailable)
  useEffect(() => {
    const checkMoodStaleness = async () => {
      // Don't check mood staleness if backend is unavailable
      if (backendError) {
        setIsMoodStale(false);
        return;
      }
      
      if (user?.id) {
        try {
          const stale = await userService.isMoodStale();
          setIsMoodStale(stale);
          // setIsMoodStale(true); // debug only
        } catch (error) {
          // Silently fail - don't show stale indicator if check fails
          setIsMoodStale(false);
        }
      }
    };

    checkMoodStaleness();
  }, [user?.id, isWellbeingModalOpen, backendError]); // Re-check when modal closes or backend status changes

  const favoritesCount = favorites.length;
  const hasAdminAccess = hasMinimumAdminAccess(user?.roles);

  const navigation = [
    // { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Chat', href: '/chat', icon: MessageSquare },
    // { name: 'Library', href: '/library', icon: Library },
    // { name: 'Breathing', href: '/breathing', icon: Wind },
    // { name: 'Booking', href: '/booking', icon: Calendar },
    { name: 'My Information', href: '/user-facts', icon: FileText },
    { name: 'My Resources', href: '/favorites', icon: Heart, badge: favoritesCount > 0 ? favoritesCount : undefined },
  ];

  const desktopNavigation = navigation;

  return (
    <div className="h-app-viewport bg-rm-bg-0 flex flex-col">
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="bg-amber-500 text-amber-900 px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>Demo Mode</span>
          <button
            onClick={signOut}
            className="underline hover:no-underline font-semibold"
          >
            Sign in
          </button>
        </div>
      )}

      {/* Navigation - Header with deep navy background */}
      <nav className="bg-rm-primary-700 text-white shadow-card border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left: Hamburger (mobile) + Logo */}
            <div className="flex items-center gap-3">
              {/* Hamburger Menu Button - Mobile Only */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden touch-target p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Logo and Connection Status */}
              <div className="flex items-center gap-2 sm:gap-4">
                <img src="/KimAvatar.png" alt="Kim Avatar" className="h-10 w-10 sm:h-11 sm:w-11 rounded-full" />
                <h1 className="text-lg sm:text-xl font-semibold text-white">Kim</h1>
                <div className="hidden sm:block">
                  <ConnectionStatus />
                </div>
              </div>

              {/* Desktop Navigation Links - Left aligned */}
              <div className="hidden md:flex items-center space-x-1">
                {desktopNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-rm-accent-500 text-rm-ink-900'
                        : 'text-white hover:text-rm-accent-300 hover:bg-white/10'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {item.name}
                    {'badge' in item && item.badge !== undefined && (
                      <span className={`ml-1.5 px-1.5 py-0.5 text-xs font-semibold rounded-full ${
                        isActive 
                          ? 'bg-rm-primary-700 text-white' 
                          : 'bg-rm-accent-500 text-rm-ink-900'
                      }`}>
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
              </div>
            </div>

            {/* Right: Quick Actions (mobile) + Desktop User Menu */}
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Mobile Quick Actions - New Session + Check-in + Feedback */}
              {currentSessionId && (
                <button
                  onClick={() => {
                    startNewSession();
                    if (location.pathname !== '/chat') {
                      navigate('/chat');
                    }
                  }}
                  className="md:hidden touch-target p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500"
                  title="New Session"
                >
                  <PenSquare className="w-5 h-5" />
                </button>
              )}

              <button
                onClick={() => setIsWellbeingModalOpen(true)}
                className={`md:hidden touch-target relative p-2 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500 ${
                  isMoodStale ? 'bg-white/10' : 'hover:bg-white/10'
                }`}
                title="Check-in"
              >
                <HeartPulse className={`w-5 h-5 ${isMoodStale ? 'animate-pulse text-rm-accent-300' : ''}`} />
                {isMoodStale && (
                  <span className="absolute top-1 right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                  </span>
                )}
              </button>

              {/* Session Feedback - Only visible in chat route on mobile when session is active */}
              {location.pathname === '/chat' && currentSessionId && (
                <button
                  onClick={() => setIsSessionFeedbackModalOpen(true)}
                  className="md:hidden touch-target p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500"
                  title="Session Feedback"
                >
                  <Star className="w-5 h-5" />
                </button>
              )}

              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="md:hidden touch-target p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500"
                title="About"
              >
                <HelpCircle className="w-5 h-5" />
              </button>

              <button
                onClick={() => setIsContactModalOpen(true)}
                className="md:hidden touch-target p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500"
                title="Contact Human"
              >
                <Phone className="w-5 h-5" />
              </button>

              {/* Desktop Actions */}
              <button
                onClick={() => setIsWellbeingModalOpen(true)}
                className={`hidden md:inline-flex relative items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isMoodStale 
                    ? 'text-rm-accent-300 bg-white/10 hover:bg-white/20' 
                    : 'text-white hover:text-rm-accent-300 hover:bg-white/10'
                }`}
                title={isMoodStale ? "Time for a wellbeing check-in!" : "Wellbeing Check-in"}
              >
                <HeartPulse className={`w-4 h-4 mr-2 ${isMoodStale ? 'animate-pulse' : ''}`} />
                Check-in
                {isMoodStale && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                  </span>
                )}
              </button>

              <button
                onClick={() => setIsContactModalOpen(true)}
                className="hidden md:inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white hover:text-rm-accent-300 hover:bg-white/10 transition-colors"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact Human
              </button>

              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="hidden md:inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white hover:text-rm-accent-300 hover:bg-white/10 transition-colors"
                title="About"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                About
              </button>

              {hasAdminAccess && (
                <Link
                  to="/admin"
                  className="hidden md:inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-rm-primary-200 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  HR
                </Link>
              )}
              <div className="hidden md:block">
                <ProfileDropdown 
                  onProfileClick={() => setIsProfileModalOpen(true)}
                  onFeedbackClick={() => setIsFeedbackModalOpen(true)}
                  showName
                />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 min-h-0 max-w-7xl mx-auto w-full px-4 sm:px-6 flex flex-col">
        <Outlet />
      </main>

      {/* Mobile Menu Drawer */}
      <Drawer
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        position="left"
        title="Menu"
      >
        <MobileMenu
          onNavigate={() => setIsMobileMenuOpen(false)}
          onViewAllSessions={() => {
            setIsMobileMenuOpen(false);
            setIsSessionsDrawerOpen(true);
          }}
          onProfileClick={() => setIsProfileModalOpen(true)}
          onFeedbackClick={() => setIsFeedbackModalOpen(true)}
          favoritesCount={favoritesCount}
        />
      </Drawer>

      {/* Sessions Drawer */}
      <SessionsDrawer
        isOpen={isSessionsDrawerOpen}
        onClose={() => setIsSessionsDrawerOpen(false)}
      />

      {/* Contact Human Modal */}
      <ContactHumanModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />

      {/* Wellbeing Check-in Modal */}
      <MoodSetupModal 
        isOpen={isWellbeingModalOpen} 
        onComplete={() => setIsWellbeingModalOpen(false)} 
      />

      {/* Profile Modal */}
      <ProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />

      {/* App Feedback Modal */}
      <AppFeedbackModal 
        isOpen={isFeedbackModalOpen} 
        onClose={() => setIsFeedbackModalOpen(false)} 
      />

      {/* Session Feedback Modal */}
      <SessionFeedbackModal
        isOpen={isSessionFeedbackModalOpen}
        onClose={() => setIsSessionFeedbackModalOpen(false)}
        sessionId={currentSessionId}
      />

      {/* Help Modal */}
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  );
};

export default Layout;
